import { useState } from "react"
import styled, { ThemeProvider, createGlobalStyle, keyframes, css } from "styled-components"
import { serviceUrl, externalServiceUrl } from '../utils/siblingSite';

// 테마 정의
const theme = {
  colors: {
    primary: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
    blue: { 500: "#3b82f6" },
    red: { 500: "#ef4444" },
    green: { 500: "#22c55e" },
    purple: { 500: "#a855f7" },
    pink: { 500: "#ec4899" },
    gray: {
      50: "#f9fafb",
      100: "#f3f4f6",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    indigo: { 500: "#6366f1" },
    orange: { 500: "#f97316" },
    emerald: { 500: "#10b981" },
    yellow: { 500: "#eab308" },
    teal: { 500: "#14b8a6" },
    cyan: { 500: "#06b6d4" },
    rose: { 500: "#f43f5e" },
    white: "#ffffff",
    black: "#000000",
  },
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
  },
  shadows: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    "2xl": "0 25px 50px -12px rgb(0 0 0 / 0.25)",
  },
  transitions: {
    default: "all 0.2s ease-in-out",
    slow: "all 0.5s ease-out",
  },
}

// 글로벌 스타일
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  button {
    font-family: inherit;
  }
`

// 애니메이션 키프레임
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideUp = keyframes`
  from {
    transform: translateX(-50%) translateY(100%) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateX(-50%) translateY(0) scale(1);
    opacity: 1;
  }
`

const slideDown = keyframes`
  from {
    transform: translateX(-50%) translateY(0) scale(1);
    opacity: 1;
  }
  to {
    transform: translateX(-50%) translateY(100%) scale(0.95);
    opacity: 0;
  }
`

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`

// 스타일드 컴포넌트들
const Container = styled.div`
  background: linear-gradient(to bottom right, #f8fafc, #f1f5f9);
  padding: 1rem;
`

const buttonVariants = {
  default: css`
    background-color: ${(props) => props.theme.colors.primary[900]};
    color: ${(props) => props.theme.colors.white};
    
    &:hover {
      background-color: ${(props) => props.theme.colors.primary[800]};
    }
  `,
  ghost: css`
    background-color: transparent;
    color: ${(props) => props.theme.colors.gray[700]};
    
    &:hover {
      background-color: ${(props) => props.theme.colors.gray[100]};
    }
  `,
}

const buttonSizes = {
  default: css`
    height: 2.5rem;
    padding: 0.5rem 1rem;
  `,
  sm: css`
    height: 2.25rem;
    padding: 0 0.75rem;
  `,
}

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: ${(props) => props.theme.transitions.default};
  border: none;
  cursor: pointer;
  
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primary[500]};
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  
  ${(props) => buttonVariants[props.variant || "default"]}
  ${(props) => buttonSizes[props.size || "default"]}
`

const LauncherButton = styled(Button)`
  position: relative;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  box-shadow: ${(props) => props.theme.shadows.lg};
  transition: all 0.3s ease;
  background-color: ${(props) => (props.isOpen ? props.theme.colors.gray[600] : props.theme.colors.gray[800])};
  transform: ${(props) => (props.isOpen ? "rotate(45deg)" : "rotate(0deg)")};
  
  &:hover {
    background-color: ${(props) => (props.isOpen ? props.theme.colors.gray[700] : props.theme.colors.gray[900])};
  }
`

const AppButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: ${(props) => props.theme.transitions.default};
  animation-delay: ${(props) => props.delay}ms;
  
  &:hover {
    background-color: ${(props) => props.theme.colors.gray[50]};
  }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
  z-index: 40;
  transition: ${(props) => props.theme.transitions.default};
  animation: ${fadeIn} 0.3s ease-out;
`

const LauncherContainer = styled.div`
  position: fixed;
  bottom: 5rem;
  left: 50%;
  z-index: 50;
  transition: all 0.5s ease-out;
  
  ${(props) =>
    props.isOpen
      ? css`
        transform: translateX(-50%) translateY(0) scale(1);
        opacity: 1;
        pointer-events: auto;
        animation: ${slideUp} 0.5s ease-out;
      `
      : css`
        transform: translateX(-50%) translateY(100%) scale(0.95);
        opacity: 0;
        pointer-events: none;
        animation: ${slideDown} 0.5s ease-out;
      `}
`

const LauncherCard = styled.div`
  padding: 1.5rem;
  box-shadow: ${(props) => props.theme.shadows["2xl"]};
  border: none;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 0.5rem;
`

const Footer = styled.footer`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgb(0 15 35);
  backdrop-filter: blur(12px);
  border-top: 1px solid ${(props) => props.theme.colors.primary[200]};
  z-index: 30;
`

const FooterContent = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  padding: 0.75rem 1rem;
`

const FooterInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const FooterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const FooterText = styled.span`
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.white};
`

const LauncherHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const LauncherTitle = styled.h3`
  font-weight: 600;
  color: ${(props) => props.theme.colors.gray[800]};
`

const AppGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  width: 20rem;
`

const AppIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background-color: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${(props) => props.theme.shadows.lg};
  transition: transform 0.2s ease;
  
  ${AppButton}:hover & {
    transform: scale(1.1);
  }
`

const AppName = styled.span`
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.gray[600]};
  font-weight: 500;
`

const NotificationDot = styled.div`
  position: absolute;
  top: -0.25rem;
  right: -0.25rem;
  width: 0.75rem;
  height: 0.75rem;
  background-color: ${(props) => props.theme.colors.red[500]};
  border-radius: 50%;
  animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`

// 아이콘 컴포넌트들
const Grid3X3 = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect width="7" height="7" x="3" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="3" rx="1" />
    <rect width="7" height="7" x="3" y="14" rx="1" />
    <rect width="7" height="7" x="14" y="14" rx="1" />
  </svg>
)


const MapPin = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

const Heart = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
  </svg>
)

const Shield = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
)

const Users = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const Cloud = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M17.5 19H7a5 5 0 1 1 6.71-4.94 6.5 6.5 0 1 1 3.79 4.94Z" />
  </svg>
)

const FolderOpen = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M2 7.5V17a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2H9.5a2 2 0 0 1-1.4-.6L6.7 6A2 2 0 0 0 5.3 5.4H4a2 2 0 0 0-2 2v.1Z" />
  </svg>
)

const MessageSquare = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
)

const UserCheck = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M16 11l2 2 4-4" />
  </svg>
)

const Play = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <polygon points="5,3 19,12 5,21" />
  </svg>
)

const MessageCircleCode = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    <path d="M10 9l-2 2 2 2" />
    <path d="M14 13l2-2-2-2" />
  </svg>
)

const Briefcase = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    <rect width="20" height="14" x="2" y="6" rx="2" />
  </svg>
)

const Database = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
    <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3" />
  </svg>
)

const GraduationCap = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </svg>
)

const FileText = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </svg>
)

const BarChart = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <line x1="12" x2="12" y1="20" y2="10" />
    <line x1="18" x2="18" y1="20" y2="4" />
    <line x1="6" x2="6" y1="20" y2="16" />
  </svg>
)
const X = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M18 6 6 18" />
    <path d="M6 6l12 12" />
  </svg>
)

const DoorOpen = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M13 4h3a2 2 0 0 1 2 2v14" />
    <path d="M2 20h20" />
    <path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z" />
    <path d="M10 12v.01" />
  </svg>
)

const Sprout = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M7 20h10" />
    <path d="M10 20c5.5-2.5.8-6.4 3-10" />
    <path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2-.4-3.5-1.4-4.3-2.6-1.1-1.5-1.5-3.5-1.3-5.5 2.4-.4 4.5.4 5.6 1.9.7.9 1.1 2 1.2 3.2" />
    <path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1.1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2" />
  </svg>
)

const Joystick = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M21 17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
    <path d="M6 15v-2" />
    <path d="M18 15v-2" />
    <path d="M12 12V6" />
    <circle cx="12" cy="4" r="2" />
  </svg>
)

const Bot = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 8V4H8" />
    <rect width="16" height="12" x="4" y="8" rx="2" />
    <path d="M2 14h2" />
    <path d="M20 14h2" />
    <path d="M15 13v2" />
    <path d="M9 13v2" />
  </svg>
)
// 앱 데이터
const apps = [
  { name: "노드마스터", icon: Shield, color: theme.colors.blue[500], href: externalServiceUrl('nodemaster.co.kr') },
  { name: "기준정보관리", icon: Users, color: theme.colors.red[500], href: serviceUrl('app') },
  { name: "클라우드", icon: Cloud, color: theme.colors.green[500], href: externalServiceUrl('skynet.re.kr') },
  { name: "파일 저장소", icon: FolderOpen, color: theme.colors.purple[500], href: externalServiceUrl('file.skynet.re.kr') },
  { name: "앤츠톡", icon: MessageSquare, color: theme.colors.pink[500], href: serviceUrl('talk') },
  { name: "앤츠 피플", icon: UserCheck, color: theme.colors.gray[500], href: serviceUrl('story') },
  { name: "웹플레이어", icon: Play, color: theme.colors.indigo[500], href: externalServiceUrl('skyscribe.co.kr') },
  { name: "앤츠미팅", icon: MessageCircleCode, color: theme.colors.orange[500], href: serviceUrl('cast') },
  { name: "서비스마켓 SI", icon: Briefcase, color: theme.colors.emerald[500], href: serviceUrl('store') },
  { name: "견적서", icon: FileText, color: theme.colors.rose[500], href: serviceUrl('estimate') },
  { name: "메타월드", icon: Joystick, color: theme.colors.yellow[500], href: serviceUrl('meta') },
  { name: "간트 차트", icon: BarChart, color: theme.colors.indigo[500] , href: serviceUrl('wbs') },
  { name: "AR대탈출", icon: DoorOpen, color: theme.colors.purple[500], href: serviceUrl('play') },
  { name: "미니그로우", icon: Sprout, color: theme.colors.teal[500], href: externalServiceUrl('minigrow.kr') },
  { name: "AI Agent", icon: Bot, color: theme.colors.cyan[500], href: serviceUrl('malbok') },
]
// 메인 컴포넌트
const LauncherFooter = () => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
        {/* 오버레이 */}
        {isOpen && <Overlay onClick={() => setIsOpen(false)} />}

        {/* 앱 런처 그리드 */}
        <LauncherContainer isOpen={isOpen}>
          <LauncherCard>
            <LauncherHeader>
              <LauncherTitle>바로가기</LauncherTitle>
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                <X size={16} />
              </Button>
            </LauncherHeader>
            <AppGrid>
              {apps.map((app, index) => (
                <AppButton
                  key={app.name}
                  delay={index * 50}
                   onClick={() => {
        window.open(app.href, '_blank', 'noopener,noreferrer');
      }}
    >
                  <AppIcon bgColor={app.color}>
                    <app.icon size={24} style={{ color: "white" }} />
                  </AppIcon>
                  <AppName>{app.name}</AppName>
                </AppButton>
              ))}
            </AppGrid>
          </LauncherCard>
        </LauncherContainer>

        {/* 하단 푸터 */}
        <Footer>
          <FooterContent>
            <FooterInner>
              <FooterSection>
                <FooterText>© 2025 Antsnest</FooterText>
              </FooterSection>

              {/* 런처 버튼 */}
              <LauncherButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
                <Grid3X3 size={20} style={{ color: "white" }} />
                {!isOpen && <NotificationDot />}
              </LauncherButton>

              <FooterSection>
                <FooterText>바로가기 런처</FooterText>
              </FooterSection>
            </FooterInner>
          </FooterContent>
        </Footer>
    </ThemeProvider>
  )
}

export default LauncherFooter